<template>
  <div
    ref="root"
    class="sc-root"
    :class="['sc-view-' + view, { 'sc-dragging': !!drag, 'sc-narrow': isNarrow }]"
    role="region"
    :aria-label="title"
  >
    <div class="sc-toolbar">
      <div class="sc-nav">
        <button type="button" class="sc-btn sc-btn-icon" :title="labels.previous" :aria-label="labels.previous" @click="move(-1)"><i class="fa fa-chevron-left"></i></button>
        <button type="button" class="sc-btn" @click="goToday">{{ labels.today }}</button>
        <button type="button" class="sc-btn sc-btn-icon" :title="labels.next" :aria-label="labels.next" @click="move(1)"><i class="fa fa-chevron-right"></i></button>
        <span class="sc-title">{{ title }}</span>
      </div>
      <div class="sc-tools">
        <slot name="toolbar"></slot>
        <div class="sc-tabs" role="tablist">
          <button
            v-for="tab in tabs"
            :key="tab.view"
            type="button"
            role="tab"
            class="sc-btn sc-tab"
            :class="{ 'sc-tab-active': view === tab.view }"
            :aria-selected="view === tab.view"
            @click="setView(tab.view)"
          >{{ tab.label }}</button>
        </div>
      </div>
    </div>
    <div v-if="loading" class="sc-loading"></div>

    <div v-if="view !== 'month'" class="sc-timegrid" :style="{ '--sc-days': range.days.length }">
      <div class="sc-head">
        <div class="sc-gutter"></div>
        <button
          v-for="day in range.days"
          :key="day.getTime()"
          type="button"
          class="sc-day-head"
          :class="{ 'sc-today': isToday(day), 'sc-weekend': isWeekend(day) }"
          @click="openDay(day)"
        >{{ formatDayHeader(day, view) }}</button>
      </div>
      <div class="sc-body" :style="{ height: bodyHeight + 'px' }">
        <div class="sc-gutter sc-hours">
          <div v-for="hour in hours" :key="hour.getTime()" class="sc-hour-label" :style="{ top: ((hour.getHours() - firstHour) * hourHeight) + 'px' }">{{ formatHour(hour) }}</div>
        </div>
        <div ref="columns" class="sc-columns" :style="{ backgroundSize: '100% ' + hourHeight + 'px' }">
          <div
            v-for="(day, i) in range.days"
            :key="day.getTime()"
            class="sc-col"
            :class="{ 'sc-today': isToday(day), 'sc-weekend': isWeekend(day) }"
            @pointerdown="onGridPointerDown($event)"
            @dblclick="onGridDblClick($event)"
          >
            <div
              v-for="seg in segments[i]"
              :key="seg.event.id"
              class="sc-event"
              :class="eventClasses(seg.event, seg)"
              :style="segmentStyle(seg)"
              role="button"
              tabindex="0"
              @pointerdown.stop="onEventPointerDown($event, seg.event, 'move')"
              @click.stop="onEventClick($event, seg.event)"
              @keydown.enter.prevent="emit('event-click', seg.event, $event)"
            >
              <div class="sc-event-time">{{ formatTime(seg.event.start) }} – {{ formatTime(seg.event.end) }}</div>
              <div class="sc-event-body">
                <slot name="event" :event="seg.event" :view="view" :compact="seg.height < 40">{{ seg.event.id }}</slot>
              </div>
              <div
                v-if="seg.event.resizable && !seg.endsAfter"
                class="sc-resize"
                @pointerdown.stop="onEventPointerDown($event, seg.event, 'resize')"
              ></div>
            </div>
            <div
              v-if="ghostSegments[i]"
              class="sc-ghost"
              :class="{ 'sc-ghost-create': drag && drag.mode === 'create' }"
              :style="{ top: ghostSegments[i].top + 'px', height: ghostSegments[i].height + 'px' }"
            >
              <span class="sc-ghost-label">{{ formatTime(drag.start) }} – {{ formatTime(drag.end) }}</span>
            </div>
            <div v-if="nowTop(day) !== null" class="sc-now" :style="{ top: nowTop(day) + 'px' }"></div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="sc-month">
      <div class="sc-month-head">
        <div v-for="day in range.days.slice(0, 7)" :key="day.getDay()" class="sc-month-weekday" :class="{ 'sc-weekend': isWeekend(day) }">{{ formatWeekday(day) }}</div>
      </div>
      <div ref="monthGrid" class="sc-month-grid">
        <div
          v-for="(day, i) in range.days"
          :key="day.getTime()"
          class="sc-month-cell"
          :class="{ 'sc-outside': day.getMonth() !== anchorMonth, 'sc-today': isToday(day), 'sc-weekend': isWeekend(day), 'sc-drop': isDropDay(day) }"
          :data-index="i"
          @dblclick="onMonthDblClick(day)"
        >
          <button type="button" class="sc-month-date" @click.stop="openDay(day)">{{ day.getDate() }}</button>
          <div
            v-for="event in monthLists[i].slice(0, maxMonthEvents)"
            :key="event.id"
            class="sc-month-event"
            :class="eventClasses(event, null)"
            role="button"
            tabindex="0"
            @pointerdown.stop="onEventPointerDown($event, event, 'move', i)"
            @click.stop="onEventClick($event, event)"
            @keydown.enter.prevent="emit('event-click', event, $event)"
          >
            <span class="sc-month-time">{{ formatTime(event.start) }}</span>
            <span class="sc-month-text"><slot name="event" :event="event" view="month" :compact="true">{{ event.id }}</slot></span>
          </div>
          <button
            v-if="monthLists[i].length > maxMonthEvents"
            type="button"
            class="sc-more"
            @click.stop="openDay(day)"
          >+{{ monthLists[i].length - maxMonthEvents }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'
import type { TimelineEvent } from '~/lib/schedule/types'
import { eventsByDay, layoutDay, rangeFor, shiftDate } from '~/lib/schedule/calendarLayout'
import type { CalendarView, DaySegment } from '~/lib/schedule/calendarLayout'
import { addUnits, startOfUnit } from '~/lib/schedule/timeScale'

type DragMode = 'move' | 'resize' | 'create'

interface CalendarDrag {
  mode: DragMode
  event: TimelineEvent | null
  start: Date
  end: Date
}

interface Labels {
  today: string
  day: string
  week: string
  month: string
  previous: string
  next: string
}

const props = withDefaults(defineProps<{
  events: TimelineEvent[]
  view?: CalendarView
  date?: Date
  weekStart?: number
  firstHour?: number
  hourHeight?: number
  snapMinutes?: number
  defaultDuration?: number
  longPressMs?: number
  maxMonthEvents?: number
  loading?: boolean
  canCreate?: boolean
  labels?: Labels
  formatTitle?: (view: CalendarView, start: Date, end: Date) => string
  formatDayHeader?: (date: Date, view: CalendarView) => string
  formatWeekday?: (date: Date) => string
  formatHour?: (date: Date) => string
  formatTime?: (date: Date) => string
}>(), {
  view: 'week',
  date: () => new Date(),
  weekStart: 1,
  firstHour: 0,
  hourHeight: 44,
  snapMinutes: 15,
  defaultDuration: 60,
  longPressMs: 350,
  maxMonthEvents: 4,
  loading: false,
  canCreate: true,
  labels: () => ({ today: 'Today', day: 'Day', week: 'Week', month: 'Month', previous: 'Previous', next: 'Next' }),
  formatTitle: (view: CalendarView, start: Date, end: Date) => view === 'month'
    ? start.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    : start.toLocaleDateString() + (view === 'week' ? ' – ' + addUnits(end, 'day', -1).toLocaleDateString() : ''),
  formatDayHeader: (date: Date) => date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'numeric' }),
  formatWeekday: (date: Date) => date.toLocaleDateString(undefined, { weekday: 'short' }),
  formatHour: (date: Date) => date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
  formatTime: (date: Date) => date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
})

const emit = defineEmits<{
  (e: 'update:view', view: CalendarView): void
  (e: 'update:date', date: Date): void
  (e: 'range', range: { start: Date, end: Date }): void
  (e: 'event-click', event: TimelineEvent, native: Event): void
  (e: 'event-change', payload: { event: TimelineEvent, start: Date, end: Date }): void
  (e: 'create', payload: { start: Date, end: Date }): void
}>()

const root = ref<HTMLElement | null>(null)
const columns = ref<HTMLElement | null>(null)
const monthGrid = ref<HTMLElement | null>(null)
const isNarrow = ref(false)
const now = ref(new Date())
const drag = shallowRef<CalendarDrag | null>(null)

const tabs = computed(() => [
  { view: 'day' as CalendarView, label: props.labels.day },
  { view: 'week' as CalendarView, label: props.labels.week },
  { view: 'month' as CalendarView, label: props.labels.month }
])

const range = computed(() => rangeFor(props.view, props.date, props.weekStart))
const anchorMonth = computed(() => props.date.getMonth())
const title = computed(() => props.formatTitle(props.view, range.value.start, range.value.end))
const bodyHeight = computed(() => (24 - props.firstHour) * props.hourHeight)
const hours = computed(() => {
  const list: Date[] = []
  for (let h = props.firstHour; h < 24; h++) {
    const d = new Date(range.value.start.getTime())
    d.setHours(h, 0, 0, 0)
    list.push(d)
  }
  return list
})

watch(range, value => emit('range', { start: value.start, end: value.end }), { immediate: true })

const visibleEvents = computed(() => {
  const start = range.value.start.getTime()
  const end = range.value.end.getTime()
  return props.events.filter(ev => ev.start.getTime() < end && ev.end.getTime() > start)
})
const dayLists = computed(() => eventsByDay(visibleEvents.value, range.value.days))
const segments = computed<DaySegment[][]>(() => {
  if (props.view === 'month') {
    return []
  }
  return range.value.days.map((day, i) => layoutDay(dayLists.value[i], day, props.firstHour, props.hourHeight))
})
const monthLists = computed(() => (props.view === 'month' ? dayLists.value : []))

const ghostSegments = computed(() => {
  const current = drag.value
  if (!current || props.view === 'month') {
    return []
  }
  const ghost: TimelineEvent = { id: '__ghost', start: current.start, end: current.end, rowKeys: [] }
  return range.value.days.map(day => layoutDay([ghost], day, props.firstHour, props.hourHeight)[0] || null)
})

const isToday = (day: Date) => startOfUnit(now.value, 'day').getTime() === day.getTime()
const isWeekend = (day: Date) => day.getDay() === 0 || day.getDay() === 6
const isDropDay = (day: Date) => {
  const current = drag.value
  if (!current || props.view !== 'month') {
    return false
  }
  const dayEnd = addUnits(day, 'day', 1).getTime()
  return current.start.getTime() < dayEnd && current.end.getTime() > day.getTime()
}

const nowTop = (day: Date) => {
  if (!isToday(day)) {
    return null
  }
  const minutes = now.value.getHours() * 60 + now.value.getMinutes() - props.firstHour * 60
  return minutes < 0 ? null : (minutes / 60) * props.hourHeight
}

const segmentStyle = (seg: DaySegment) => ({
  top: seg.top + 'px',
  height: seg.height + 'px',
  left: 'calc(' + (seg.col / seg.cols) * 100 + '% + 2px)',
  width: 'calc(' + (1 / seg.cols) * 100 + '% - 4px)'
})

const eventClasses = (event: TimelineEvent, seg: DaySegment | null) => [
  event.className,
  {
    'sc-event-draggable': event.draggable,
    'sc-event-dragging': drag.value && drag.value.event === event,
    'sc-event-short': seg && seg.height < 40,
    'sc-clipped-start': seg && seg.startsBefore,
    'sc-clipped-end': seg && seg.endsAfter
  }
]

// Navigation

const setView = (view: CalendarView) => emit('update:view', view)
const move = (direction: number) => emit('update:date', shiftDate(props.view, props.date, direction))
const goToday = () => emit('update:date', startOfUnit(new Date(), 'day'))
const openDay = (day: Date) => {
  emit('update:date', day)
  emit('update:view', 'day')
}

// Pointer interactions

interface Pending {
  pointerId: number
  pointerType: string
  x: number
  y: number
  mode: DragMode
  event: TimelineEvent | null
  originDay: number
  originMinutes: number
  active: boolean
  timer: ReturnType<typeof setTimeout> | null
}

let pending: Pending | null = null
let suppressClick = false
let lastPointer = { x: 0, y: 0 }
let scrollParent: HTMLElement | null = null
let autoScrollFrame = 0
const snapMs = () => props.snapMinutes * 60000

const gridPoint = (x: number, y: number) => {
  if (props.view === 'month') {
    const el = document.elementFromPoint(x, y)
    const cell = el && (el as HTMLElement).closest ? (el as HTMLElement).closest('.sc-month-cell') as HTMLElement | null : null
    const index = cell && cell.dataset.index !== undefined ? Number(cell.dataset.index) : (pending ? pending.originDay : 0)
    return { day: index, minutes: 0 }
  }
  const el = columns.value
  if (!el) {
    return { day: 0, minutes: 0 }
  }
  const rect = el.getBoundingClientRect()
  const count = range.value.days.length
  const day = Math.max(0, Math.min(count - 1, Math.floor(((x - rect.left) / rect.width) * count)))
  const span = (24 - props.firstHour) * 60
  const minutes = props.firstHour * 60 + Math.max(0, Math.min(span - 1, ((y - rect.top) / props.hourHeight) * 60))
  return { day, minutes }
}

const timeAt = (day: number, minutes: number) => {
  const d = new Date(range.value.days[day].getTime())
  d.setHours(0, Math.round(minutes), 0, 0)
  return d
}

const snapMinutesValue = (minutes: number, mode: 'round' | 'floor' | 'ceil') => {
  const step = props.snapMinutes
  return Math[mode](minutes / step) * step
}

const shiftEvent = (date: Date, days: number, minutes: number) => {
  const d = addUnits(date, 'day', days)
  return new Date(d.getTime() + minutes * 60000)
}

const activate = () => {
  if (!pending) {
    return
  }
  pending.active = true
  if (pending.timer) {
    clearTimeout(pending.timer)
    pending.timer = null
  }
  if (pending.pointerType === 'touch' && navigator.vibrate) {
    navigator.vibrate(10)
  }
  scrollParent = findScrollParent(root.value)
  if (pending.mode === 'create') {
    const start = timeAt(pending.originDay, snapMinutesValue(pending.originMinutes, 'floor'))
    drag.value = { mode: 'create', event: null, start, end: new Date(start.getTime() + props.defaultDuration * 60000) }
  } else if (pending.event) {
    drag.value = { mode: pending.mode, event: pending.event, start: pending.event.start, end: pending.event.end }
  }
  startAutoScroll()
}

const update = (x: number, y: number) => {
  if (!pending || !pending.active || !drag.value) {
    return
  }
  const point = gridPoint(x, y)
  const event = pending.event
  if (pending.mode === 'move' && event) {
    const days = point.day - pending.originDay
    const minutes = props.view === 'month' ? 0 : snapMinutesValue(point.minutes - pending.originMinutes, 'round')
    drag.value = { mode: 'move', event, start: shiftEvent(event.start, days, minutes), end: shiftEvent(event.end, days, minutes) }
  } else if (pending.mode === 'resize' && event) {
    const minutes = snapMinutesValue((point.day - pending.originDay) * 1440 + point.minutes - pending.originMinutes, 'round')
    let end = new Date(event.end.getTime() + minutes * 60000)
    if (end.getTime() < event.start.getTime() + snapMs()) {
      end = new Date(event.start.getTime() + snapMs())
    }
    drag.value = { mode: 'resize', event, start: event.start, end }
  } else if (pending.mode === 'create') {
    const origin = timeAt(pending.originDay, pending.originMinutes)
    const current = timeAt(point.day, point.minutes)
    const forward = current.getTime() >= origin.getTime()
    const start = forward
      ? timeAt(pending.originDay, snapMinutesValue(pending.originMinutes, 'floor'))
      : timeAt(point.day, snapMinutesValue(point.minutes, 'floor'))
    let end = forward
      ? timeAt(point.day, snapMinutesValue(point.minutes, 'ceil'))
      : timeAt(pending.originDay, snapMinutesValue(pending.originMinutes, 'ceil'))
    if (end.getTime() - start.getTime() < snapMs()) {
      end = new Date(start.getTime() + snapMs())
    }
    drag.value = { mode: 'create', event: null, start, end }
  }
}

const begin = (e: PointerEvent, mode: DragMode, event: TimelineEvent | null, monthIndex?: number) => {
  if (e.pointerType === 'mouse' && e.button !== 0) {
    return
  }
  cancel()
  const point = monthIndex !== undefined ? { day: monthIndex, minutes: 0 } : gridPoint(e.clientX, e.clientY)
  pending = {
    pointerId: e.pointerId,
    pointerType: e.pointerType,
    x: e.clientX,
    y: e.clientY,
    mode,
    event,
    originDay: point.day,
    originMinutes: point.minutes,
    active: false,
    timer: null
  }
  lastPointer = { x: e.clientX, y: e.clientY }
  if (e.pointerType !== 'mouse') {
    pending.timer = setTimeout(activate, props.longPressMs)
  }
  window.addEventListener('pointermove', onPointerMove)
  window.addEventListener('pointerup', onPointerUp)
  window.addEventListener('pointercancel', onPointerCancel)
  window.addEventListener('keydown', onKeyDown)
}

const onEventPointerDown = (e: PointerEvent, event: TimelineEvent, mode: DragMode, monthIndex?: number) => {
  if (!event.draggable || (mode === 'resize' && !event.resizable)) {
    return
  }
  begin(e, mode, event, monthIndex)
}

const onGridPointerDown = (e: PointerEvent) => {
  if (!props.canCreate) {
    return
  }
  begin(e, 'create', null)
}

const onPointerMove = (e: PointerEvent) => {
  if (!pending || e.pointerId !== pending.pointerId) {
    return
  }
  lastPointer = { x: e.clientX, y: e.clientY }
  if (!pending.active) {
    const distance = Math.hypot(e.clientX - pending.x, e.clientY - pending.y)
    if (pending.pointerType === 'mouse') {
      if (distance > 4) {
        activate()
      }
    } else if (distance > 8) {
      cancel()
      return
    }
  }
  update(e.clientX, e.clientY)
}

const onPointerUp = (e: PointerEvent) => {
  if (!pending || e.pointerId !== pending.pointerId) {
    return
  }
  const wasActive = pending.active
  const current = drag.value
  cancel()
  if (!wasActive || !current) {
    return
  }
  suppressClick = true
  setTimeout(() => { suppressClick = false }, 0)
  if (current.mode === 'create') {
    emit('create', { start: current.start, end: current.end })
  } else if (current.event && (current.start.getTime() !== current.event.start.getTime() || current.end.getTime() !== current.event.end.getTime())) {
    emit('event-change', { event: current.event, start: current.start, end: current.end })
  }
}

const onPointerCancel = (e: PointerEvent) => {
  if (pending && e.pointerId === pending.pointerId) {
    cancel()
  }
}

const onKeyDown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    cancel()
  }
}

function cancel () {
  if (pending && pending.timer) {
    clearTimeout(pending.timer)
  }
  pending = null
  drag.value = null
  stopAutoScroll()
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
  window.removeEventListener('pointercancel', onPointerCancel)
  window.removeEventListener('keydown', onKeyDown)
}

const onEventClick = (e: MouseEvent, event: TimelineEvent) => {
  if (suppressClick) {
    return
  }
  emit('event-click', event, e)
}

const onGridDblClick = (e: MouseEvent) => {
  if (!props.canCreate || (e.target as HTMLElement).closest('.sc-event')) {
    return
  }
  const point = gridPoint(e.clientX, e.clientY)
  const start = timeAt(point.day, snapMinutesValue(point.minutes, 'floor'))
  emit('create', { start, end: new Date(start.getTime() + props.defaultDuration * 60000) })
}

const onMonthDblClick = (day: Date) => {
  if (!props.canCreate) {
    return
  }
  const start = new Date(day.getTime())
  start.setHours(Math.max(props.firstHour, 9), 0, 0, 0)
  emit('create', { start, end: new Date(start.getTime() + props.defaultDuration * 60000) })
}

// Touch drags must block native scrolling once active
const onTouchMove = (e: TouchEvent) => {
  if (pending && pending.active) {
    e.preventDefault()
  }
}

function findScrollParent (el: HTMLElement | null): HTMLElement | null {
  let node = el ? el.parentElement : null
  while (node) {
    const style = getComputedStyle(node)
    if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight) {
      return node
    }
    node = node.parentElement
  }
  return document.scrollingElement as HTMLElement | null
}

function startAutoScroll () {
  stopAutoScroll()
  const step = () => {
    if (!pending || !pending.active) {
      return
    }
    const el = scrollParent
    if (el) {
      const isDocument = el === document.scrollingElement
      const top = isDocument ? 0 : el.getBoundingClientRect().top
      const bottom = isDocument ? window.innerHeight : el.getBoundingClientRect().bottom
      const edge = 40
      let delta = 0
      if (lastPointer.y < top + edge) {
        delta = -Math.ceil((top + edge - lastPointer.y) / 3)
      } else if (lastPointer.y > bottom - edge) {
        delta = Math.ceil((lastPointer.y - (bottom - edge)) / 3)
      }
      if (delta) {
        el.scrollTop += delta
        update(lastPointer.x, lastPointer.y)
      }
    }
    autoScrollFrame = requestAnimationFrame(step)
  }
  autoScrollFrame = requestAnimationFrame(step)
}

function stopAutoScroll () {
  if (autoScrollFrame) {
    cancelAnimationFrame(autoScrollFrame)
    autoScrollFrame = 0
  }
}

let nowTimer: ReturnType<typeof setInterval> | null = null
let resizeObserver: ResizeObserver | null = null

onMounted(() => {
  nowTimer = setInterval(() => { now.value = new Date() }, 60000)
  if (root.value) {
    root.value.addEventListener('touchmove', onTouchMove, { passive: false })
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(entries => {
        isNarrow.value = entries[0].contentRect.width < 600
      })
      resizeObserver.observe(root.value)
    }
  }
})

onBeforeUnmount(() => {
  cancel()
  if (nowTimer) {
    clearInterval(nowTimer)
  }
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
  if (root.value) {
    root.value.removeEventListener('touchmove', onTouchMove)
  }
})
</script>

<style scoped>
.sc-root {
  --sc-bg: #ffffff;
  --sc-header-bg: #f8f9fb;
  --sc-border: #e3e7ec;
  --sc-grid: #eef1f4;
  --sc-text: #26313d;
  --sc-muted: #6b7785;
  --sc-weekend: rgba(30, 41, 59, 0.035);
  --sc-today: rgba(205, 220, 57, 0.2);
  --sc-now: #e5484d;
  --sc-accent: #3499db;
  --sc-radius: 6px;
  --sc-gutter: 52px;
  --sc-font: 'Oxygen', Arial, sans-serif;
  position: relative;
  background: var(--sc-bg);
  border: 1px solid var(--sc-border);
  border-radius: 8px;
  font-family: var(--sc-font);
  color: var(--sc-text);
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}
.sc-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px;
  border-bottom: 1px solid var(--sc-border);
  border-radius: 8px 8px 0 0;
  background: var(--sc-header-bg);
}
.sc-nav, .sc-tools, .sc-tabs {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
.sc-tabs {
  gap: 0;
}
.sc-title {
  margin-left: 8px;
  font-weight: 700;
  font-size: 14px;
}
.sc-btn {
  height: 28px;
  padding: 0 10px;
  border: 1px solid var(--sc-border);
  border-radius: var(--sc-radius);
  background: #fff;
  color: var(--sc-text);
  font-size: 12px;
  line-height: 26px;
  cursor: pointer;
}
.sc-btn:hover {
  background: #f1f4f7;
}
.sc-btn:focus-visible {
  outline: 2px solid var(--sc-accent);
  outline-offset: 1px;
}
.sc-btn-icon {
  width: 28px;
  padding: 0;
}
.sc-tab {
  border-radius: 0;
  margin-left: -1px;
}
.sc-tab:first-child {
  border-radius: var(--sc-radius) 0 0 var(--sc-radius);
}
.sc-tab:last-child {
  border-radius: 0 var(--sc-radius) var(--sc-radius) 0;
}
.sc-tab-active, .sc-tab-active:hover {
  background: var(--sc-accent);
  border-color: var(--sc-accent);
  color: #fff;
  position: relative;
}
.sc-loading {
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  z-index: 20;
  background: linear-gradient(90deg, transparent, var(--sc-accent), transparent);
  background-size: 50% 100%;
  animation: sc-loading 1.1s linear infinite;
}
@keyframes sc-loading {
  from { background-position: -50% 0; }
  to { background-position: 150% 0; }
}

/* Day and week */
.sc-head {
  position: sticky;
  top: 0;
  z-index: 6;
  display: grid;
  grid-template-columns: var(--sc-gutter) repeat(var(--sc-days), minmax(0, 1fr));
  background: var(--sc-header-bg);
  border-bottom: 1px solid var(--sc-border);
}
.sc-day-head {
  height: 32px;
  padding: 0 4px;
  border: 0;
  border-left: 1px solid var(--sc-grid);
  background: transparent;
  color: var(--sc-text);
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}
.sc-day-head:hover {
  background: #edf1f5;
}
.sc-day-head.sc-today {
  background: var(--sc-today);
}
.sc-body {
  position: relative;
  display: grid;
  grid-template-columns: var(--sc-gutter) minmax(0, 1fr);
}
.sc-hours {
  position: relative;
}
.sc-hour-label {
  position: absolute;
  right: 6px;
  margin-top: 2px;
  font-size: 11px;
  color: var(--sc-muted);
  white-space: nowrap;
}
.sc-columns {
  position: relative;
  display: grid;
  grid-template-columns: repeat(var(--sc-days), minmax(0, 1fr));
  background-image: linear-gradient(to bottom, var(--sc-grid) 1px, transparent 1px);
}
.sc-col {
  position: relative;
  border-left: 1px solid var(--sc-grid);
  touch-action: pan-x pan-y;
}
.sc-col.sc-weekend {
  background: var(--sc-weekend);
}
.sc-col.sc-today {
  background: var(--sc-today);
}
.sc-event {
  position: absolute;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding: 2px 5px;
  overflow: hidden;
  border-radius: var(--sc-radius);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-left: 3px solid var(--sc-accent);
  background: #e8f1fb;
  color: var(--sc-text);
  font-size: 12px;
  line-height: 1.3;
  z-index: 1;
  cursor: pointer;
  transition: box-shadow 0.12s ease;
}
.sc-event:hover {
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.15);
  z-index: 2;
}
.sc-event:focus-visible {
  box-shadow: 0 0 0 2px var(--sc-accent);
  outline: none;
  z-index: 3;
}
.sc-event-draggable {
  cursor: grab;
}
.sc-dragging, .sc-dragging .sc-event-draggable {
  cursor: grabbing;
}
.sc-event-dragging {
  opacity: 0.35;
}
.sc-clipped-start {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}
.sc-clipped-end {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}
.sc-event-time {
  font-size: 11px;
  opacity: 0.8;
  white-space: nowrap;
}
.sc-event-body {
  min-width: 0;
  overflow: hidden;
}
.sc-event-short {
  flex-direction: row;
  align-items: center;
  gap: 4px;
}
.sc-event-short .sc-event-body {
  white-space: nowrap;
  text-overflow: ellipsis;
}
.sc-resize {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 8px;
  cursor: ns-resize;
}
.sc-event:hover .sc-resize::after {
  content: '';
  position: absolute;
  left: 40%;
  right: 40%;
  bottom: 2px;
  height: 2px;
  border-radius: 1px;
  background: rgba(0, 0, 0, 0.25);
}
@media (pointer: coarse) {
  .sc-resize {
    height: 14px;
  }
}
.sc-ghost {
  position: absolute;
  left: 2px;
  right: 2px;
  z-index: 4;
  box-sizing: border-box;
  border-radius: var(--sc-radius);
  border: 2px dashed var(--sc-accent);
  background: rgba(52, 153, 219, 0.14);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.18);
  pointer-events: none;
}
.sc-ghost-create {
  border-style: solid;
  border-color: #76b841;
  background: rgba(118, 184, 65, 0.18);
}
.sc-ghost-label {
  display: block;
  padding: 2px 5px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.sc-now {
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  margin-top: -1px;
  background: var(--sc-now);
  opacity: 0.8;
  z-index: 3;
  pointer-events: none;
}
.sc-now::before {
  content: '';
  position: absolute;
  left: -4px;
  top: -3px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--sc-now);
}

/* Month */
.sc-month-head {
  position: sticky;
  top: 0;
  z-index: 6;
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  background: var(--sc-header-bg);
  border-bottom: 1px solid var(--sc-border);
}
.sc-month-weekday {
  padding: 8px 6px;
  font-size: 12px;
  font-weight: 700;
  color: var(--sc-muted);
  text-transform: capitalize;
}
.sc-month-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  grid-auto-rows: minmax(110px, auto);
}
.sc-month-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding: 2px 3px 4px;
  border-left: 1px solid var(--sc-grid);
  border-bottom: 1px solid var(--sc-grid);
}
.sc-month-cell.sc-weekend {
  background: var(--sc-weekend);
}
.sc-month-cell.sc-today {
  background: var(--sc-today);
}
.sc-month-cell.sc-outside {
  color: var(--sc-muted);
  background: #fafbfc;
}
.sc-month-cell.sc-drop {
  box-shadow: inset 0 0 0 2px var(--sc-accent);
  background: rgba(52, 153, 219, 0.08);
}
.sc-month-date {
  align-self: flex-end;
  min-width: 24px;
  height: 22px;
  padding: 0 4px;
  border: 0;
  border-radius: 11px;
  background: transparent;
  color: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.sc-month-date:hover {
  background: #e3e7ec;
}
.sc-month-event {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  padding: 1px 4px;
  border-radius: 4px;
  border-left: 3px solid var(--sc-accent);
  background: #e8f1fb;
  font-size: 11px;
  line-height: 18px;
  white-space: nowrap;
  overflow: hidden;
  cursor: pointer;
}
.sc-month-event:hover {
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.15);
}
.sc-month-time {
  flex: none;
  font-weight: 700;
}
.sc-month-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sc-more {
  align-self: flex-start;
  border: 0;
  background: transparent;
  color: var(--sc-accent);
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  padding: 0 4px;
}
.sc-narrow {
  --sc-gutter: 40px;
}
.sc-narrow .sc-title {
  flex-basis: 100%;
  margin: 4px 0 0;
  order: 5;
}
.sc-narrow .sc-month-grid {
  grid-auto-rows: minmax(72px, auto);
}
.sc-narrow .sc-month-time {
  display: none;
}
.sc-narrow .sc-event {
  padding: 1px 3px;
  font-size: 11px;
}
</style>
